import { getP } from 'redux-polyglot'
import { SEARCH_INPUT_KEYWORD } from '../constants/search'
import api from '../libs/api'

/**
 * Actions for retrieving a list of trending keywords
 */
export const SEARCH_TRENDING_REQUEST = 'SEARCH/TRENDING/REQUEST'
export const SEARCH_TRENDING_SUCCESS = 'SEARCH/TRENDING/SUCCESS'
export const SEARCH_TRENDING_FAILURE = 'SEARCH/TRENDING/FAILURE'

const searchTrendingRequest = () => ({
  type: SEARCH_TRENDING_REQUEST
})

const searchTrendingSuccess = results => ({
  type: SEARCH_TRENDING_SUCCESS,
  payload: {
    results
  }
})

const searchTrendingFailure = error => ({
  type: SEARCH_TRENDING_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const loadTrendingKeywords = () => (dispatch, getState) => {
  dispatch(searchTrendingRequest())
  return api.getTrendingKeywords()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(searchTrendingSuccess(response.keywords))
        } else {
          return dispatch(searchTrendingFailure(response.reason))
        }
      },
      () => dispatch(searchTrendingFailure(getP(getState()).t("tv_search.error_retrieving_trending_keywords", { _: "An error occurred while retrieving the list of trending keywords" })))
    )
}

/**
 * Actions for retrieving a list of recent keywords
 */
export const SEARCH_RECENT_REQUEST = 'SEARCH/RECENT/REQUEST'
export const SEARCH_RECENT_SUCCESS = 'SEARCH/RECENT/SUCCESS'
export const SEARCH_RECENT_FAILURE = 'SEARCH/RECENT/FAILURE'

const searchRecentRequest = () => ({
  type: SEARCH_RECENT_REQUEST
})

const searchRecentSuccess = results => ({
  type: SEARCH_RECENT_SUCCESS,
  payload: {
    results
  }
})

const searchRecentFailure = error => ({
  type: SEARCH_RECENT_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const loadRecentKeywords = () => (dispatch, getState) => {
  dispatch(searchRecentRequest())
  return api.getRecentKeywords()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(searchRecentSuccess(response.keywords))
        } else {
          return dispatch(searchRecentFailure(response.reason))
        }
      },
      () => dispatch(searchRecentFailure(getP(getState()).t("tv_search.error_retrieving_recent_keywords", { _: "An error occurred while retrieving the list of recent keywords" })))
    )
}

/**
 * Actions for retrieving a list of suggested keywords
 */
export const SEARCH_SUGGESTED_CLEAR = 'SEARCH/SUGGESTED/CLEAR'
export const SEARCH_SUGGESTED_REQUEST = 'SEARCH/SUGGESTED/REQUEST'
export const SEARCH_SUGGESTED_SUCCESS = 'SEARCH/SUGGESTED/SUCCESS'
export const SEARCH_SUGGESTED_FAILURE = 'SEARCH/SUGGESTED/FAILURE'

export const clearSuggestedKeywords = () => ({
  type: SEARCH_SUGGESTED_CLEAR
})

const searchSuggestedRequest = () => ({
  type: SEARCH_SUGGESTED_REQUEST
})

const searchSuggestedSuccess = results => ({
  type: SEARCH_SUGGESTED_SUCCESS,
  payload: {
    results
  }
})

const searchSuggestedFailure = error => ({
  type: SEARCH_SUGGESTED_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const performLoadSuggestedKeywords = keyword => (dispatch, getState) => {
  dispatch(searchSuggestedRequest())
  return api.getSuggestedKeywords(keyword)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(searchSuggestedSuccess(response.keywords))
        } else {
          return dispatch(searchSuggestedFailure(response.reason))
        }
      },
      () => dispatch(searchSuggestedFailure(getP(getState()).t("tv_search.error_retrieving_suggested_keywords", { _: "An error occurred while retrieving the list of suggested keywords" })))
    )
}

let suggestedKeywordsTimer = null
const clearSuggestedKeywordsTimer = () => {
  if (suggestedKeywordsTimer !== null) {
    clearTimeout(suggestedKeywordsTimer)
    suggestedKeywordsTimer = null
  }
}

export const loadSuggestedKeywords = keyword => dispatch => {
  clearSuggestedKeywordsTimer()
  suggestedKeywordsTimer = setTimeout(() => {
    dispatch(performLoadSuggestedKeywords(keyword))
  }, 1000)
}


/**
 * Actions for performing the search
 */
export const SEARCH_REQUEST = 'SEARCH/REQUEST'
export const SEARCH_SUCCESS = 'SEARCH/SUCCESS'
export const SEARCH_FAILURE = 'SEARCH/FAILURE'

const searchRequest = keyword => ({
  type: SEARCH_REQUEST,
  payload: keyword
})

const searchSuccess = results => ({
  type: SEARCH_SUCCESS,
  payload: results
})

const searchFailure = (keyword, error) => ({
  type: SEARCH_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
  meta: {
    keyword
  }
})

const processResults = results => {
  const channels = []
  if (typeof results.channels !== 'undefined' && results.channels.length > 0) {
    results.channels.forEach(sourceChannel => {
      channels.push({
        id: sourceChannel.id,
        no: sourceChannel.stb_number,
        name: sourceChannel.name,
        img: sourceChannel.img.medium
      })
    })
  }

  const videos = []
  if (typeof results.videos !== 'undefined' && results.videos.length > 0) {
    results.videos.forEach(sourceVideo => {
      videos.push({
        id: sourceVideo.id,
        name: sourceVideo.name,
        img: sourceVideo.img.medium
      })
    })
  }

  return {
    channels,
    videos
  }
}

export const search = keyword => (dispatch, getState) => {
  dispatch(searchRequest(keyword))
  return api.search(keyword)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(searchSuccess(processResults(response.results)))
        } else {
          return dispatch(searchFailure(keyword, response.reason))
        }
      },
      () => dispatch(searchFailure(keyword, getP(getState()).t("tv_search.error_perform_search", { _: "An error occurred while performing the search" })))
    )
}

/**
 * Actions related to keyboard on search screen
 */
export const FOCUS_INPUT = 'SEARCH/FOCUS_INPUT'
export const UPDATE_INPUT = 'SEARCH/UPDATE_INPUT'
export const DELETE_INPUT = 'SEARCH/DELETE_INPUT'
export const ERROR_INPUT = 'SEARCH/ERROR_INPUT'
export const SET_STATUS = 'SEARCH/SET_STATUS'
export const FOCUS_KEYBOARD = 'SEARCH/FOCUS_KEYBOARD'
export const START_OVER = 'SEARCH/START/OVER'
export const LOAD_SUGGESTION = 'SEARCH/LOAD/SUGGESTION'

export const focusInput = (focusedInput, focusedRow, focusedCol) => {

  let newFocusedInput
  switch(focusedInput) {
    case SEARCH_INPUT_KEYWORD:
      newFocusedInput = focusedInput
      break;
    default:
      newFocusedInput = SEARCH_INPUT_KEYWORD
  }

  return {
    type: FOCUS_INPUT,
    payload: {
      focusedInput: newFocusedInput,
      focusedRow: focusedRow,
      focusedCol: focusedCol
    }
  }
}

export const updateInput = newKey => ({
  type: UPDATE_INPUT,
  payload: {
    newKey
  }
})

export const deleteInput = () => ({
  type: DELETE_INPUT
})

export const errorInput = (input, error) => ({
  type: ERROR_INPUT,
  payload: {
    input,
    error
  }
})


export const setStatus = (status) => ({
  type: SET_STATUS,
  payload: {
    status
  }
})

export const focusKeyboard = (focus) => ({
  type: FOCUS_KEYBOARD,
  payload: {
    focus
  }
})

export const startOver = () => ({
  type: START_OVER
})

export const loadSuggestion = keyword => ({
  type: LOAD_SUGGESTION,
  payload: keyword
})
